import { useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { useGlobal } from 'src/contexts/GlobalContext';
import FileUpload from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload';
import { FileData } from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload/FileUpload';
import { sanitizeFileName } from 'src/utils/sanitize';

import { AddFileDrawer } from './AddFileDrawer';
import { SelectAssetLink } from './AssetPicker.styled';
import { AssetPickerDialog } from './AssetPickerDialog';
import { useAssetLibraryUpload } from './useAssetLibraryUpload';
import { assetToPreviewFileData } from './utils';

export interface AssetSelectFieldProps {
  value?: string | null;
  onChange: (assetId: string | null) => void;
  assets?: FanguardApiContractsClientAssetModel[];
  isAssetsLoading?: boolean;
  accept?: string;
  maxSizeInMB?: number;
  minWidth?: number;
  minHeight?: number;
  description?: string;
  dialogTitle?: string;
}

/**
 * Asset field for forms that store a reference to the library instead of the file itself. Picking an
 * existing asset only passes its id along, and uploading a new file adds it to the library first.
 */
export const AssetSelectField = ({
  value,
  onChange,
  assets = [],
  isAssetsLoading,
  accept,
  maxSizeInMB,
  // FileUpload asks for at least 48px of height by default, which would rule out small icons here.
  minWidth = 0,
  minHeight = 0,
  description,
  dialogTitle,
}: AssetSelectFieldProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { message } = App.useApp();
  const { uploadFileToLibrary } = useAssetLibraryUpload();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isAddFileOpen, setIsAddFileOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<FileData | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedAsset = assets.find((asset) => asset.id === value);
  const preview = selectedAsset ? assetToPreviewFileData(selectedAsset) : (pendingPreview ?? undefined);

  const openPicker = () => {
    setIsAddFileOpen(false);
    setIsPickerOpen(true);
  };

  // Clicking the dragger's own input keeps FileUpload's accept and size validation
  // instead of duplicating it behind a second file input.
  const openDeviceFilePicker = () => {
    setIsAddFileOpen(false);
    wrapperRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  };

  const handleSelectAssetClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openPicker();
  };

  // Files dropped or picked straight on the field bypass the dialog, so they are added to the
  // library here before the form can reference them.
  const handleFieldChange = async (fileData: FileData | string | null) => {
    if (!fileData || typeof fileData === 'string') {
      setPendingPreview(null);
      onChange(null);
      return;
    }

    const name = sanitizeFileName(fileData.name || '');
    const existingAsset = assets.find((asset) => sanitizeFileName(asset.name || '') === name);

    if (existingAsset) {
      message.info(t('assets.existingAssetReused', { name }));
      onChange(existingAsset.id ?? null);
      return;
    }

    setPendingPreview(fileData);
    setIsUploading(true);

    try {
      const created = await uploadFileToLibrary(fileData, { resolveAsset: true });
      onChange(created?.id ?? null);

      if (!created?.id) setPendingPreview(null);
    } catch {
      // The upload mutation surfaces its own error message.
      setPendingPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePickerSelect = (_fileData: FileData, asset?: FanguardApiContractsClientAssetModel) => {
    setPendingPreview(null);
    onChange(asset?.id ?? null);
  };

  const label = isDesktop ? (
    <>
      {`${t('assets.dropFileHereOr')} `}
      <SelectAssetLink onClick={handleSelectAssetClick} data-testid="select-asset-link">
        {t('assets.selectAsset')}
      </SelectAssetLink>
    </>
  ) : (
    `${t('assets.dropFileHereOr')} ${t('assets.selectAsset')}`
  );

  return (
    <div ref={wrapperRef}>
      <FileUpload
        value={preview}
        onChange={handleFieldChange}
        accept={accept}
        maxSizeInMB={maxSizeInMB}
        minWidth={minWidth}
        minHeight={minHeight}
        description={description}
        label={label}
        isLoading={isUploading}
        openFileDialogOnClick={false}
        onClick={isDesktop ? openPicker : () => setIsAddFileOpen(true)}
      />
      {!isDesktop && (
        <AddFileDrawer
          open={isAddFileOpen}
          onClose={() => setIsAddFileOpen(false)}
          onChooseFromLibrary={openDeviceFilePicker}
          onSelectFromAssets={openPicker}
        />
      )}
      <AssetPickerDialog
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handlePickerSelect}
        assets={assets}
        isLoading={isAssetsLoading}
        title={dialogTitle}
        accept={accept}
        maxSizeInMB={maxSizeInMB}
        minWidth={minWidth}
        minHeight={minHeight}
        selectedAssetId={value}
        selectionMode="reference"
      />
    </div>
  );
};
